import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useIntl } from 'react-intl';
import {
  Button,
  TextInput,
  PasswordInput,
  Title,
  Stack,
  Flex,
  Anchor,
  Divider,
  Container,
  Center,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { myFetch } from '@commons/fetch';
import routes from '@commons/routes';
import { useUser } from '@contexts/UserContext';
import { Link as ReactRouterLink } from 'react-router';

export const UserLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const navigate = useNavigate();
  const intl = useIntl();
  const { user, login, logoutWithCallback } = useUser();

  useEffect(() => {
    const validateToken = async () => {
      if (!user) {
        setIsValidating(false);
        return;
      }

      try {
        setIsValidating(true);
        const response = await myFetch(`/users/token/verify`, {
          method: 'GET',
        });

        const { user } = response.data;

        navigate(routes.dashboard(user.username), { replace: true });
      } catch (error: any) {
        notifications.show({
          title: intl.formatMessage({ id: 'error' }),
          message: error.message,
          color: 'red',
        });

        logoutWithCallback(() => {});

        setIsValidating(false);
      }
    };

    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async () => {
    if (!username || !password) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({
          id: 'pleaseEnterUsernameAndPassword',
        }),
        color: 'red',
      });
      return;
    }

    try {
      setIsLoading(true);

      const response = await myFetch(`/users/authenticate`, {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });

      const { token, user } = response.data;

      login(token, user);

      navigate(routes.dashboard(user.username), { replace: true });
    } catch (err) {
      setIsLoading(false);
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({
          id: 'notAuthorized',
        }),
        color: 'red',
      });
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  // Show nothing while validating existing session
  if (isValidating) {
    return null;
  }

  return (
    <Center mih="100svh" p="md">
      <Container size="xs" maw="100svw">
        <Stack gap="md">
          <Title order={2}>
            {intl.formatMessage({
              id: 'userLogin',
              defaultMessage: 'User Login',
            })}
          </Title>

          <TextInput
            label={intl.formatMessage({
              id: 'username',
              defaultMessage: 'Username',
            })}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={isLoading}
            autoFocus
          />

          <PasswordInput
            label={intl.formatMessage({
              id: 'password',
              defaultMessage: 'Password',
            })}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={isLoading}
          />

          <Flex justify="space-between" align="center" mt="xs">
            <Anchor
              component={ReactRouterLink}
              to={routes.routes.userRegister}
              size="sm"
            >
              {intl.formatMessage({
                id: 'createAccount',
                defaultMessage: 'Create Account',
              })}
            </Anchor>

            <Button
              id="login-button"
              variant="filled"
              onClick={handleLogin}
              loading={isLoading}
            >
              {intl.formatMessage({ id: 'access' })}
            </Button>
          </Flex>

          <Divider my="md" />

          <Anchor component={ReactRouterLink} to={routes.root} size="sm">
            {intl.formatMessage({
              id: 'backToHome',
              defaultMessage: 'Back to Home',
            })}
          </Anchor>
        </Stack>
      </Container>
    </Center>
  );
};
